import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { LoadingSpinner, EmptyState } from '../components/layout/SharedComponents';
import api from '../services/api';
import { toast } from 'sonner';
import {
  AlertTriangle, Bell, ShieldAlert, Package, Clock,
  ArrowUpDown, ArrowUp, ArrowDown, CheckCircle, XCircle, Info,
} from 'lucide-react';

function SortHeader({ label, sortKey, current, onSort, className = '' }) {
  const active = current.key === sortKey;
  const icon = active
    ? (current.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
    : <ArrowUpDown className="w-3 h-3 opacity-30" />;
  return (
    <th className={`px-3 py-2 font-semibold select-none cursor-pointer hover:bg-accent/30 transition-colors ${className}`}
      onClick={() => onSort(sortKey)}>
      <span className="inline-flex items-center gap-1">{label} {icon}</span>
    </th>
  );
}

function sortRows(data, key, dir) {
  if (!key) return data;
  return [...data].sort((a, b) => {
    let av = a[key] ?? '', bv = b[key] ?? '';
    if (typeof av === 'number' && typeof bv === 'number') return dir === 'asc' ? av - bv : bv - av;
    return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
}

const SEV_CONFIG = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700', icon: XCircle, iconColor: 'text-red-500' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', icon: AlertTriangle, iconColor: 'text-amber-500' },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700', icon: Info, iconColor: 'text-blue-500' },
};

export default function InventoryAlerts({ onItemClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [sort, setSort] = useState({ key: 'severity', dir: 'asc' });
  const toggleSort = (key) => setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/inventory/alerts');
      setData(r.data);
    } catch {
      toast.error('Failed to load inventory alerts');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;
  if (!data || data.summary?.total === 0) {
    return (
      <div data-testid="alerts-empty">
        <EmptyState
          title="All Clear"
          description="No inventory alerts at this time. Stock levels are healthy and no batches are near expiry."
        />
      </div>
    );
  }

  const { summary, alerts } = data;

  let filtered = alerts;
  if (filter !== 'ALL') filtered = filtered.filter(a => a.severity === filter);
  if (typeFilter !== 'ALL') filtered = filtered.filter(a => a.type === typeFilter);

  // Custom sort for severity
  const sorted = sort.key === 'severity'
    ? [...filtered].sort((a, b) => {
        const order = { critical: 0, warning: 1, info: 2 };
        const diff = (order[a.severity] || 9) - (order[b.severity] || 9);
        return sort.dir === 'asc' ? diff : -diff;
      })
    : sortRows(filtered, sort.key, sort.dir);

  const stockAlerts = summary.stock_out + summary.stock_critical + summary.stock_low;
  const batchAlerts = summary.batch_expired + summary.batch_expiring;

  return (
    <div className="space-y-4" data-testid="inventory-alerts">
      {/* Summary banner */}
      <div className={`rounded-xl p-4 border-2 ${summary.critical > 0 ? 'border-red-300 bg-red-50/70' : summary.warning > 0 ? 'border-amber-300 bg-amber-50/70' : 'border-green-300 bg-green-50/70'}`}>
        <div className="flex items-center gap-3 mb-3">
          <Bell className={`h-5 w-5 ${summary.critical > 0 ? 'text-red-500' : summary.warning > 0 ? 'text-amber-500' : 'text-green-500'}`} />
          <h2 className="font-bold text-lg">
            {summary.total} Active Alert{summary.total !== 1 ? 's' : ''}
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <AlertSummaryCard
            label="Critical"
            count={summary.critical}
            color="red"
            active={filter === 'critical'}
            onClick={() => setFilter(filter === 'critical' ? 'ALL' : 'critical')}
          />
          <AlertSummaryCard
            label="Warning"
            count={summary.warning}
            color="amber"
            active={filter === 'warning'}
            onClick={() => setFilter(filter === 'warning' ? 'ALL' : 'warning')}
          />
          <AlertSummaryCard
            label="Info"
            count={summary.info}
            color="blue"
            active={filter === 'info'}
            onClick={() => setFilter(filter === 'info' ? 'ALL' : 'info')}
          />
          <AlertSummaryCard
            label="Stock Issues"
            count={stockAlerts}
            color="slate"
            icon={ShieldAlert}
            active={typeFilter === 'STOCK'}
            onClick={() => setTypeFilter(typeFilter === 'STOCK' ? 'ALL' : 'STOCK')}
            isType
          />
          <AlertSummaryCard
            label="Batch/Expiry"
            count={batchAlerts}
            color="purple"
            icon={Package}
            active={typeFilter === 'BATCH'}
            onClick={() => setTypeFilter(typeFilter === 'BATCH' ? 'ALL' : 'BATCH')}
            isType
          />
        </div>
      </div>

      {/* Type filter buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground font-semibold">Type:</span>
        {[
          { id: 'ALL', label: `All (${summary.total})` },
          { id: 'STOCK_OUT', label: `Out of Stock (${summary.stock_out})` },
          { id: 'STOCK_CRITICAL', label: `Critical Stock (${summary.stock_critical})` },
          { id: 'STOCK_LOW', label: `Low Stock (${summary.stock_low})` },
          { id: 'BATCH_EXPIRED', label: `Expired Batches (${summary.batch_expired})` },
          { id: 'BATCH_EXPIRING', label: `Expiring Batches (${summary.batch_expiring})` },
        ].filter(f => f.id === 'ALL' || summary[f.id.toLowerCase()] > 0).map(f => (
          <Button key={f.id} size="sm" variant={typeFilter === f.id ? 'default' : 'outline'}
            className="h-7 text-xs px-3"
            onClick={() => setTypeFilter(typeFilter === f.id ? 'ALL' : f.id)}
            data-testid={`alert-type-${f.id}`}>
            {f.label}
          </Button>
        ))}
      </div>

      {/* Alerts table */}
      <div className="border rounded-xl overflow-hidden">
        <table className="w-full text-xs" data-testid="alerts-table">
          <thead><tr className="bg-muted/50 border-b">
            <SortHeader label="Severity" sortKey="severity" current={sort} onSort={toggleSort} className="text-left w-20" />
            <SortHeader label="Type" sortKey="type" current={sort} onSort={toggleSort} className="text-left" />
            <SortHeader label="Item Code" sortKey="item_code" current={sort} onSort={toggleSort} className="text-left" />
            <th className="text-left px-3 py-2 font-semibold">Description</th>
            <SortHeader label="Location" sortKey="location" current={sort} onSort={toggleSort} className="text-left" />
            <th className="text-left px-3 py-2 font-semibold">Alert Message</th>
          </tr></thead>
          <tbody>
            {sorted.map((alert, i) => {
              const config = SEV_CONFIG[alert.severity] || SEV_CONFIG.info;
              const Icon = config.icon;
              return (
                <tr key={i}
                  className={`border-b cursor-pointer hover:bg-accent/20 transition-colors ${config.bg}`}
                  onClick={() => onItemClick?.(alert.item_code)}
                  data-testid={`alert-row-${i}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <Icon className={`h-3.5 w-3.5 ${config.iconColor}`} />
                      <Badge className={`text-[8px] ${config.badge}`}>{alert.severity.toUpperCase()}</Badge>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="text-[8px]">
                      {alert.type.replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 font-mono font-semibold text-primary">{alert.item_code}</td>
                  <td className="px-3 py-2 max-w-[160px] truncate text-muted-foreground">{alert.description}</td>
                  <td className="px-3 py-2 font-mono text-xs">{alert.location}</td>
                  <td className="px-3 py-2">
                    <span className={`font-medium ${config.text}`}>{alert.message}</span>
                    {alert.batch_number && (
                      <span className="ml-1 text-muted-foreground text-[10px]">({alert.batch_number})</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-6 text-muted-foreground text-sm">
          No alerts matching the current filters.
        </div>
      )}

      {/* Refresh button */}
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={load} data-testid="refresh-alerts-btn">
          <Bell className="h-3.5 w-3.5 mr-1" /> Refresh Alerts
        </Button>
      </div>
    </div>
  );
}


function AlertSummaryCard({ label, count, color, icon: Icon, active, onClick, isType }) {
  const colors = {
    red: { bg: 'bg-red-100', text: 'text-red-700', ring: 'ring-red-400' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-700', ring: 'ring-amber-400' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-700', ring: 'ring-blue-400' },
    slate: { bg: 'bg-slate-100', text: 'text-slate-700', ring: 'ring-slate-400' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-700', ring: 'ring-purple-400' },
  };
  const c = colors[color] || colors.slate;

  return (
    <div
      className={`rounded-lg p-2.5 cursor-pointer transition-all border ${c.bg} ${active ? `ring-2 ${c.ring} shadow-sm` : 'hover:shadow-sm'}`}
      onClick={onClick}
      data-testid={`alert-summary-${label.toLowerCase().replace(/[/ ]/g, '-')}`}>
      <div className="flex items-center gap-1.5">
        {Icon ? <Icon className={`h-3.5 w-3.5 ${c.text}`} /> : null}
        <span className={`text-xl font-bold ${c.text}`}>{count}</span>
      </div>
      <p className={`text-[10px] font-semibold ${c.text} mt-0.5`}>{label}</p>
    </div>
  );
}
